import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRightIcon } from "@heroicons/react/24/outline";

import type { ApiUser } from "context/UserContext";

type SecuritySectionProps = {
  user: ApiUser;
  hasPasswordAuth: boolean;
  isRequestingVerification: boolean;
  onSetupRequest: (payload: {
    method: "email";
    target: string;
    intent: "verify_email_and_set_password";
  }) => void;
};

const SecuritySection: React.FC<SecuritySectionProps> = ({
  user,
  hasPasswordAuth,
  isRequestingVerification,
  onSetupRequest,
}) => {
  const navigate = useNavigate();

  const emailExists = !!user.email;
  const emailVerified = user.email_verified;

  return (
    <div className="rounded-xl border border-oat-200 bg-white p-6 shadow-sm">
      <h2 className="mb-4 font-serif text-[19px] font-normal text-soil">
        Security
      </h2>
      <div className="space-y-2">
        {hasPasswordAuth && (
          <button
            onClick={() => navigate("/set-password")}
            className="group flex w-full items-center justify-between rounded-lg px-0 py-2 text-left transition-colors"
          >
            <div>
              <div className="font-medium text-soil group-hover:underline">
                Change Password
              </div>
              <div className="text-sm text-oat-700">
                Update your account password
              </div>
            </div>
            <ArrowRightIcon className="h-5 w-5 shrink-0 text-oat-700 transition-transform group-hover:translate-x-1" />
          </button>
        )}

        {/* Empty auth_providers means an email invitee who never set a password. */}
        {!hasPasswordAuth && emailExists && emailVerified && (
          <button
            onClick={() => navigate("/set-password")}
            className="group flex w-full items-center justify-between rounded-xl border border-cantelope/40 bg-cantelope/10 px-4 py-3 text-left ring-1 ring-cantelope/20 transition-colors hover:bg-cantelope/20"
          >
            <div>
              <div className="font-semibold text-soil group-hover:underline">
                Set a password
              </div>
              <div className="text-sm text-oat-700">
                Add a password so you can log in without a one-time link.
              </div>
            </div>
            <ArrowRightIcon className="h-5 w-5 shrink-0 text-soil transition-transform group-hover:translate-x-1" />
          </button>
        )}

        {!hasPasswordAuth && emailExists && !emailVerified && (
          <button
            onClick={() =>
              onSetupRequest({
                method: "email",
                target: user.email!,
                intent: "verify_email_and_set_password",
              })
            }
            disabled={isRequestingVerification}
            className="group flex w-full items-center justify-between rounded-lg px-0 py-2 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-50"
          >
            <div className="min-w-0 break-words">
              <div className="font-medium text-soil group-hover:underline">
                Set up Email & Password Login
              </div>
              <div className="text-sm text-oat-700">
                Verify {user.email} to enable password login.
              </div>
            </div>
            <ArrowRightIcon className="h-5 w-5 shrink-0 text-oat-700 transition-transform group-hover:translate-x-1" />
          </button>
        )}

        {!hasPasswordAuth && !emailExists && (
          <div className="py-2 text-sm text-oat-700">
            Add an email address in the Profile section first to enable password
            login.
          </div>
        )}
      </div>
    </div>
  );
};

export default SecuritySection;
